/**
 * Buyer country detected by Shopify from the request IP. The store is a
 * single-market (VN) store, so localization.country is always VN in Liquid and
 * cannot be used. Cached per session; resolves to "" when detection fails, and
 * callers treat "" as "unknown" and keep the Vietnam behaviour.
 *
 * @returns {Promise<string>} ISO country code, or "" if unknown.
 */
window.marouDetectedCountry = function () {
  const cacheKey = "marou_detected_country";

  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached !== null) return Promise.resolve(cached);
  } catch (error) {
    // sessionStorage blocked (private mode) — fall through to the fetch.
  }

  return fetch("/browsing_context_suggestions.json")
    .then((res) => res.json())
    .then((data) => {
      const country = data?.detected_values?.country?.handle || "";
      try {
        sessionStorage.setItem(cacheKey, country);
      } catch (error) {
        // sessionStorage blocked — detection just repeats on the next page.
      }
      return country;
    })
    .catch(() => "");
};

/**
 * @param {string} country
 * @returns {boolean} true when the buyer is in Vietnam or the country is unknown.
 */
window.marouIsVietnamOrUnknown = function (country) {
  return !country || country === "VN";
};

/**
 *  @class
 *  @function ShopLocation
 */
if (!customElements.get("shop-location")) {
  class ShopLocation extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // this.render();
      this.openModalHandler();
      this.submitFormModal();
      this.wireStoreSelect();
      this.wireDrawerFocus();
      // this.renderShopName();
    }

    /**
     * Dialog focus management for #Location-Drawer.
     *
     * The drawer opens from several places (the header and product triggers via
     * openModal, plus the geolocation auto-detect in location-drawer.liquid) and
     * closes from several more (the shared <side-panel-close>, Escape, the
     * backdrop). Watching the .active class covers every one of them without
     * touching the shared panel code in app.js.
     */
    wireDrawerFocus() {
      const drawer = document.getElementById("Location-Drawer");
      if (!drawer) return;

      const FOCUSABLE = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(", ");

      // getClientRects() rather than offsetParent: the drawer is position:fixed,
      // and it also has to skip the store select when that block is not rendered.
      const focusableItems = () =>
        [...drawer.querySelectorAll(FOCUSABLE)].filter(
          (el) => el.getClientRects().length > 0
        );

      drawer.addEventListener("keydown", (e) => {
        if (e.key !== "Tab") return;

        const items = focusableItems();
        if (!items.length) return;

        const first = items[0];
        const last = items[items.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });

      let wasActive = drawer.classList.contains("active");
      let lastFocused = null;

      new MutationObserver(() => {
        const isActive = drawer.classList.contains("active");
        if (isActive === wasActive) return;
        wasActive = isActive;

        if (isActive) {
          lastFocused = document.activeElement;
          // Focus the panel itself so the dialog name and description are
          // announced; the close button is the first stop from there. Deferred
          // by a frame so the element is visible by the time focus() runs.
          requestAnimationFrame(() => drawer.focus());
        } else if (lastFocused && document.contains(lastFocused)) {
          lastFocused.focus();
          lastFocused = null;
        }
      }).observe(drawer, { attributes: true, attributeFilter: ["class"] });
    }

    shouldShowTooltip() {
      const lastHidden = localStorage.getItem("tooltip_last_hidden");
      if (!lastHidden) return true;
      
      const now = Date.now();
      const fourteenDays = 14 * 24 * 60 * 60 * 1000;
      
      return (now - lastHidden) > fourteenDays;
    }
    
    hideTooltipForPeriod() {
      const now = Date.now();
      localStorage.setItem("tooltip_last_hidden", now);
      
      document.querySelectorAll('.order-from-inner__tooltip').forEach((tooltip) => {
        tooltip.classList.remove('active');
      });
    }

    async getDefaultLocation() {
      const response = await fetch("https://maisonmarou.com/apps/meow-multi-location-support/storefront/locations", {
        headers: {
          "content-type": "application/json",
          "x-shop-domain": "marou-vn.myshopify.com",
        },
        body: JSON.stringify({
          first: 250,
          nodes: `
                id
                name
                shipsInventory
                address {
                  address1
                  address2
                  city
                  country
                  zip
                  phone
                }
              `,
        }),
        method: "POST",
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const locations = data.nodes || [];

      const defaultLocation = locations.find((location) => location.shipsInventory);

      if (defaultLocation) {
        return {
          id: defaultLocation.id,
          name: defaultLocation.name,
        };
      } else {
        //console.log("Không tìm thấy cửa hàng mặc định.");
        return null;
      }
    }


    render() {
      const $target = document.querySelector("meow-locations");
      const $provinceSelect = document.getElementById("select-location");
      const $shopSelect = document.getElementById("select-store");
      let fetchData = [
        { address: { city: "Loading..." }, id: "", name: "Loading..." },
      ];

      $target.addEventListener("status", (e) => {
        const { name, data } = e.detail;

        if (name === "loaded") {
          fetchData = data.nodes;

          const provinceData = [
            ...new Set(data.nodes.map((node) => node.address.city)),
          ].map((city) => ({
            value: city,
            text: city,
          }));

          updateOptions($provinceSelect, provinceData);

          if (localStorage.getItem("province_id") && provinceData.find((node) => node.value === localStorage.getItem("province_id"))) {
            $provinceSelect.value = localStorage.getItem("province_id");
            $provinceSelect.dispatchEvent(new Event("change"));
          } else if (provinceData[0]) {
            $provinceSelect.value = provinceData[0].value;
            $provinceSelect.dispatchEvent(new Event("change"));
          }
        } else {
          updateOptions($provinceSelect, [
            { value: "Loading...", text: "Loading..." },
          ]);
        }
      });

      $provinceSelect.addEventListener("change", function () {
        localStorage.setItem("province_id", this.value);

        const shopData = fetchData
          .filter((node) => node.address.city === this.value)
          .map((node) => ({
            value: node.id,
            text: node.name,
          }));

        updateOptions($shopSelect, shopData);

        if (localStorage.getItem("shop_id") && shopData.find((node) => node.value === localStorage.getItem("shop_id"))) {
          $shopSelect.value = localStorage.getItem("shop_id");
        } else if (shopData[0]) {
          $shopSelect.value = shopData[0].value;
        }
      });

      function updateOptions(target, options) {
        target.innerHTML = "";
        let changed = false;

        options.forEach((option) => {
          const $option = document.createElement("option");
          $option.value = option.value;
          $option.textContent = option.text;
          if (option.selected) {
            $option.selected = true;
            changed = true;
          }
          target.appendChild($option);
        });

        if (changed) {
          target.dispatchEvent(new Event("change"));
        }
      }
    }

    openModalHandler() {
      const target = [
        ...document.querySelectorAll(
          ".shop-location-text-container, order-from-inner"
        ),
      ];
      target.forEach((el) => {
        el.addEventListener("click", () => {
          this.openModal();
        });
      });
    }

    openModal() {
      const shop_location_modal = document.getElementById('Location-Drawer');
      if (!shop_location_modal.classList.contains("active")) {
        shop_location_modal.classList.add("active");
        document.body.classList.add("open-cc");
      }
    }

    // Hàm này được gọi khi người dùng chọn một chi nhánh
    // selectedShopGid có giá trị là "gid://shopify/Location/72045527248"
    onShopLocationSelect(selectedShopGid, options = { reload: true }) {

      // Hiển thị thông báo cho người dùng biết hệ thống đang lưu lựa chọn
      //console.log('Đang lưu lựa chọn chi nhánh:', selectedShopGid);

      // Chuẩn bị dữ liệu để gửi đi.
      // Shopify yêu cầu key phải nằm trong một đối tượng 'attributes'
      const storeSelect = document.getElementById('select-store');
      const data = {
        attributes: {
          'shop_id': selectedShopGid,
          'shop_id_trust': true,
          'store_id': storeSelect ? storeSelect.value : '',
        }
      };

      // Gửi yêu cầu POST đến endpoint /cart/update.js của Shopify
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
        .then(response => response.json())
        .then(cart => {
          //console.log('Đã lưu lựa chọn vào giỏ hàng thành công!', cart);
          // Bạn có thể thực hiện các hành động khác ở đây, ví dụ:
          // Cập nhật lại giao diện hiển thị tồn kho ngay lập tức
          // Hoặc tải lại trang để Liquid có thể đọc giá trị mới

          this.hideTooltipForPeriod();

          if (options.reload !== false) {
            // Keep the cart intact on province change — just reload so Liquid
            // re-evaluates stock/availability for the newly selected city.
            window.location.reload();
          }
        })
        .catch(() => {});
    }

    submitFormModal() {
      const button = document.querySelector(".submit-modal");
      const select_location = document.getElementById("select-location");
      const order_from_inner_close = document.querySelectorAll(".order-from-inner-close");

      button.addEventListener("click", () => {
        if (button.classList.contains("loading")) return;
        button.classList.add("loading");
        this.onShopLocationSelect(select_location.value);
      });

      order_from_inner_close.forEach((el) => {
        el.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.hideTooltipForPeriod();
          this.onShopLocationSelect(select_location.value, { reload: false });
        });
      });
    }

    // Cascade the "Pick a store" dropdown from the selected province.
    // Options are server-rendered from the geo_store_directory metafield; this
    // just filters them to the chosen city and preselects the saved store_id.
    wireStoreSelect() {
      // Scope to the live drawer so the province/store pair binds explicitly.
      const province = document.querySelector('.location-drawer #select-location');
      const store = document.querySelector('.location-drawer #select-store');
      if (!province || !store) return;

      // Product pages render extra <shop-location> elements, so this runs several
      // times. Initialise once, capturing the full server-rendered list before any
      // rebuild shrinks it (otherwise later runs would leave only one province).
      if (store.dataset.wired) return;
      store.dataset.wired = '1';

      const saved = store.dataset.selected || '';
      const anyLabel = store.dataset.anyLabel || '';
      const all = Array.from(store.options)
        .filter((o) => o.value)
        .map((o) => ({ value: o.value, city: o.dataset.city, name: o.textContent.trim() }));

      const rebuild = () => {
        const city = province.value;
        store.innerHTML = '';
        store.appendChild(new Option(anyLabel, ''));
        all
          .filter((s) => s.city === city)
          .forEach((s) => {
            const opt = new Option(s.name, s.value);
            if (s.value === saved) opt.selected = true;
            store.appendChild(opt);
          });
      };

      // Fires on manual change and on geolocation auto-select (which dispatches 'change').
      province.addEventListener('change', rebuild);
      rebuild();
    }

    async renderShopName() {
      if (!localStorage.getItem("shop_name") || !localStorage.getItem("shop_id")) {
        // Nếu chưa có, gọi API để lấy defaultLocation
        const defaultLocation = await this.getDefaultLocation();
        //console.log(defaultLocation);
        // Nếu có defaultLocation thì set vào localStorage
        if (defaultLocation) {
          localStorage.setItem("shop_name", defaultLocation.name);
          localStorage.setItem("shop_id", defaultLocation.id);
        } else {
          // fallback nếu API lỗi hoặc không có data
          localStorage.setItem("shop_name", "Flagship Saigon (D1)");
          localStorage.setItem("shop_id", "gid://shopify/Location/72045428944");
        }
      }

      const shop_location_text = document.querySelector(".shop_location_value");
      const order_from_content = [
        ...document.querySelectorAll(".order-from-content"),
      ];

      if (shop_location_text) {
        shop_location_text.innerHTML = localStorage.getItem("shop_name");
      }
      order_from_content.forEach((el) => {
        el.querySelector("span").innerHTML = localStorage.getItem("shop_name");
      });
    }
  }

  customElements.define("shop-location", ShopLocation);
}

document.addEventListener("DOMContentLoaded", () => {
  const shopLocationInstance = document.querySelector('shop-location');
  
  // The "Order from / Shipping from <city>" chips and their tooltip are about
  // Vietnam stock — only buyers detected in Vietnam (or undetected) get them.
  // The class hides the chips (see .country-outside-vn in app.css); theme.liquid
  // applies it before first paint when the country is already cached.
  window.marouDetectedCountry().then((country) => {
    const insideVietnam = window.marouIsVietnamOrUnknown(country);
    document.documentElement.classList.toggle("country-outside-vn", !insideVietnam);
    if (!insideVietnam) return;

    if (document.querySelector('.announcement-bar__order-from .order-from-inner__tooltip')) {
      if (!shopLocationInstance || shopLocationInstance.shouldShowTooltip()) {
        document.querySelector('.announcement-bar__order-from .order-from-inner__tooltip').classList.add('active');
      }
    }

    if (document.querySelector('.thb-mobile-menu-order-from .order-from-inner__tooltip')) {
      if (!shopLocationInstance || shopLocationInstance.shouldShowTooltip()) {
        document.querySelector('.thb-mobile-menu-order-from .order-from-inner__tooltip').classList.add('active');
      }
    }
  });

  document.querySelectorAll('.mobile-toggle').forEach(el => {
    el.addEventListener('click', () => {
      window.marouDetectedCountry().then((country) => {
        if (!window.marouIsVietnamOrUnknown(country)) return;
        if (!shopLocationInstance || shopLocationInstance.shouldShowTooltip()) {
          document.querySelector('.mobile-menu-drawer--inner .order-from-inner__tooltip').classList.add('active');
        }
      });
    });
  });

  document.addEventListener('click', function(event) {
    var link = event.target.closest('[data-availability-drawer]');
    if (!link) return;
    event.preventDefault();
    if (window.inventoryInfo) window.inventoryInfo.showAvailabilityDrawer(event);
  });

})